// components/shared/SignUp/MultiStepSignup/types.ts
import * as Yup from 'yup';
import { UserRole } from '@/types';
import { isPhoneRegistered } from '@/api/auth'; // Импортируем функцию для проверки телефона

// Вспомогательная функция для debounce
const debounce = (func: Function, delay: number) => {
  let timeout: NodeJS.Timeout;
  return (...args: any[]) => {
    clearTimeout(timeout);
    return new Promise((resolve) => {
      timeout = setTimeout(async () => {
        resolve(await func(...args));
      }, delay);
    });
  };
};

// Дебаунсированная версия isPhoneRegistered
const debouncedIsPhoneRegistered = debounce(async (phone: string) => {
  try {
    const response = await isPhoneRegistered(phone);
    return response.data.isRegistered;
  } catch (error) {
    console.error('Error checking phone registration:', error);
    // В случае ошибки API, считаем, что номер не зарегистрирован, чтобы не блокировать пользователя
    // или можно вернуть true, чтобы предотвратить регистрацию при проблемах с API
    return false;
  }
}, 500); // Задержка 500 мс

export interface IMultiStepSignupValues {
  // Крок 1: Основна інформація
  role: UserRole;
  firstname: string;
  lastname: string;
  email: string;
  password: string;
  confirmPassword: string;

  // Крок 2: Робоча інформація (тільки для manager)
  companyName?: string;
  department?: string;
  jobTitle?: string;

  // Крок 3: Додаткова інформація
  province: string;
  phone: string;
  ageGroup: string;
  agreeToTerms: boolean;
}

// Провінції Канади
export const CANADIAN_PROVINCES = [
  { value: 'AB', label: 'Alberta' },
  { value: 'BC', label: 'British Columbia' },
  { value: 'MB', label: 'Manitoba' },
  { value: 'NB', label: 'New Brunswick' },
  { value: 'NL', label: 'Newfoundland and Labrador' },
  { value: 'NS', label: 'Nova Scotia' },
  { value: 'ON', label: 'Ontario' },
  { value: 'PE', label: 'Prince Edward Island' },
  { value: 'QC', label: 'Quebec' },
  { value: 'SK', label: 'Saskatchewan' },
  { value: 'NT', label: 'Northwest Territories' },
  { value: 'NU', label: 'Nunavut' },
  { value: 'YT', label: 'Yukon' },
];

// Вікові групи
export const AGE_GROUPS = [
  { value: '18-25', label: '18-25 years' },
  { value: '26-35', label: '26-35 years' },
  { value: '36-45', label: '36-45 years' },
  { value: '46-55', label: '46-55 years' },
  { value: '56+', label: '56+ years' },
];

// Початкові значення
export const getDefaultValues = (role: UserRole): IMultiStepSignupValues => ({
  role,
  firstname: '',
  lastname: '',
  email: '',
  password: '',
  confirmPassword: '',
  ...(role === UserRole.MANAGER && {
    companyName: '',
    department: '',
    jobTitle: '',
  }),
  province: '',
  phone: '',
  ageGroup: '',
  agreeToTerms: false,
});

// Функція для визначення кількості кроків
export const getTotalSteps = (role: UserRole): number => {
  return role === UserRole.MANAGER ? 3 : 2;
};

// Функція для визначення полів кожного кроку
export const getStepFields = (step: number, role: UserRole): string[] => {
  if (role === UserRole.MANAGER) {
    switch (step) {
      case 1: return ['role', 'firstname', 'lastname', 'email', 'password', 'confirmPassword'];
      case 2: return ['companyName', 'department', 'jobTitle'];
      case 3: return ['province', 'phone', 'ageGroup', 'agreeToTerms'];
      default: return [];
    }
  } else { // owner
    switch (step) {
      case 1: return ['role', 'firstname', 'lastname', 'email', 'password', 'confirmPassword'];
      case 2: return ['province', 'phone', 'ageGroup', 'agreeToTerms'];
      default: return [];
    }
  }
};

// Схеми валідації
const step1ValidationSchema = Yup.object().shape({
  role: Yup.string<UserRole>().oneOf([UserRole.OWNER, UserRole.MANAGER]).required('Please select your role'),
  firstname: Yup.string().required('First name is required').min(2),
  lastname: Yup.string().required('Last name is required').min(2),
  email: Yup.string().required('Email is required').email(),
  password: Yup.string().required('Password is required').min(7).max(128).matches(/(?=.*[A-Z])/, 'Must contain an uppercase letter').matches(/(?=.*\d)|(?=.*\W+)/, 'Must contain a number or special character'),
  confirmPassword: Yup.string().required('Confirm password is required').oneOf([Yup.ref('password')], 'Passwords must match'),
});

const step2ValidationSchema = Yup.object().shape({
  companyName: Yup.string().required('Company name is required').min(2),
  department: Yup.string().required('Department is required').min(2),
  jobTitle: Yup.string().required('Job title is required').min(2),
});

const step3ValidationSchema = Yup.object().shape({
  province: Yup.string().required('Province/Territory is required'),
  phone: Yup.string()
    .required('Phone is required')
    .matches(/^[\+]?[1-9][\d]{0,15}$/, 'Please enter a valid phone number')
    .test(
      'is-phone-unique',
      'This phone number is already registered',
      async function (value) {
        if (!value) return true; // Пропускаем валидацию, если поле пустое
        const isRegistered = await debouncedIsPhoneRegistered(value);
        return !isRegistered;
      }
    ),
  ageGroup: Yup.string().required('Age group is required'),
  agreeToTerms: Yup.boolean().oneOf([true], 'You must agree to the terms').required('You must agree to the terms'),
});

export const getValidationSchemaForStep = (step: number, role: UserRole) => {
  if (role === UserRole.MANAGER) {
    switch (step) {
      case 1: return step1ValidationSchema;
      case 2: return step2ValidationSchema;
      case 3: return step3ValidationSchema;
      default: return Yup.object().shape({});
    }
  } else { // owner
    switch (step) {
      case 1: return step1ValidationSchema;
      case 2: return step3ValidationSchema;
      default: return Yup.object().shape({});
    }
  }
};
